import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle, AlarmClock, Bell, CheckCircle2, Clock, ExternalLink,
  Hourglass, Inbox, RefreshCw, Users,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TYPE_META = {
  emd_t3: { label: "EMD T-3", icon: Hourglass, accent: "border-l-amber-500" },
  emd_t1: { label: "EMD T-1", icon: AlertTriangle, accent: "border-l-red-600" },
  bid_t2: { label: "Bid T-2", icon: AlarmClock, accent: "border-l-amber-500" },
  pending_review: { label: "Pending Review", icon: Inbox, accent: "border-l-blue-600" },
  expired: { label: "Auto-Expired", icon: CheckCircle2, accent: "border-l-slate-400" },
  overdue_followup: { label: "Overdue Follow-up", icon: Clock, accent: "border-l-red-500" },
  dup_lead_activity: { label: "Duplicate Lead", icon: Users, accent: "border-l-blue-500" },
  strong_match: { label: "Strong Match", icon: Bell, accent: "border-l-emerald-500" },
  invoice_brokerage: { label: "Invoice Brokerage", icon: CheckCircle2, accent: "border-l-emerald-600" },
  sla_breach: { label: "SLA Breach", icon: AlertTriangle, accent: "border-l-orange-500" },
  emd_critical_active_match: { label: "EMD Critical (active match)", icon: AlertTriangle, accent: "border-l-red-700" },
  lead_status_suggestion: { label: "Lead Status Suggestion", icon: Inbox, accent: "border-l-purple-500" },
};

const SEVERITY_BG = {
  critical: "bg-red-50",
  warning: "bg-amber-50",
  info: "bg-white",
};

function ReminderCard({ n, onAck }) {
  const meta = TYPE_META[n.type] || { label: n.type, icon: Bell, accent: "border-l-slate-400" };
  const Icon = meta.icon;
  return (
    <div
      className={`relative border border-slate-200 border-l-4 ${meta.accent} ${SEVERITY_BG[n.severity] || "bg-white"} rounded-md p-4 shadow-sm flex flex-col gap-3`}
      data-testid={`reminder-card-${n.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-700" />
          <span className="label-eyebrow !text-slate-700">{meta.label}</span>
          {n.severity === "critical" && (
            <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white uppercase">
              Urgent
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 font-mono-num">{formatDateTime(n.created_at)}</div>
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-900">{n.title}</div>
        <div className="text-sm text-slate-700 mt-1 leading-relaxed">{n.message}</div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        {n.asset_id && (
          <Link
            to={`/assets/${n.asset_id}`}
            className="text-xs font-semibold text-slate-900 inline-flex items-center gap-1 hover:underline"
            data-testid={`reminder-view-asset-${n.id}`}
          >
            View asset <ExternalLink className="h-3 w-3" />
          </Link>
        )}
        {n.lead_id && (
          <Link
            to={`/leads/${n.lead_id}`}
            className="text-xs font-semibold text-slate-900 inline-flex items-center gap-1 hover:underline"
            data-testid={`reminder-view-lead-${n.id}`}
          >
            View lead <ExternalLink className="h-3 w-3" />
          </Link>
        )}
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAck(n.id)}
          data-testid={`reminder-ack-${n.id}`}
        >
          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Dismiss
        </Button>
      </div>
    </div>
  );
}

export default function Reminders() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [showAck, setShowAck] = React.useState(false);

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications", showAck],
    queryFn: async () => {
      const { data } = await api.get("/notifications", {
        params: { include_acknowledged: showAck },
      });
      return data;
    },
  });

  const ack = useMutation({
    mutationFn: async (id) => api.post(`/notifications/${id}/acknowledge`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      toast.success("Dismissed");
    },
  });

  const refresh = useMutation({
    mutationFn: async () => api.post("/notifications/run-automations"),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      const s = res?.data?.stats || {};
      toast.success(
        `Automations run — EMD T-3:${s.emd_t3 || 0} · EMD T-1:${s.emd_t1 || 0} · Bid T-2:${s.bid_t2 || 0} · Expired:${s.expired || 0}`
      );
    },
  });

  const grouped = React.useMemo(() => {
    const map = { emd: [], bid: [], pending: [], expired: [], leads: [], pipeline: [] };
    for (const n of notifications) {
      if (n.type.startsWith("emd") || n.type === "emd_critical_active_match") map.emd.push(n);
      else if (n.type === "bid_t2") map.bid.push(n);
      else if (n.type === "pending_review") map.pending.push(n);
      else if (n.type === "expired") map.expired.push(n);
      else if (n.type === "overdue_followup" || n.type === "dup_lead_activity") map.leads.push(n);
      else if (["strong_match", "invoice_brokerage", "sla_breach", "lead_status_suggestion"].includes(n.type)) map.pipeline.push(n);
    }
    return map;
  }, [notifications]);

  const counts = {
    all: notifications.length,
    emd: grouped.emd.length,
    bid: grouped.bid.length,
    pending: grouped.pending.length,
    expired: grouped.expired.length,
    leads: grouped.leads.length,
    pipeline: grouped.pipeline.length,
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Action Center</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Reminders</h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Outputs from the daily automations: EMD due-date escalation, bid submission alerts, pending review
            queue{user?.role === "admin" ? "" : " (your assets)"} and auto-expired auctions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={showAck}
              onChange={(e) => setShowAck(e.target.checked)}
              data-testid="show-acknowledged-toggle"
            />
            Show dismissed
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            data-testid="run-automations-button"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refresh.isPending ? "animate-spin" : ""}`} />
            Run automations
          </Button>
        </div>
      </div>

      <Tabs defaultValue="all" className="mt-8">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="all" data-testid="tab-reminders-all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="emd" data-testid="tab-reminders-emd">EMD ({counts.emd})</TabsTrigger>
          <TabsTrigger value="bid" data-testid="tab-reminders-bid">Bid ({counts.bid})</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-reminders-leads">Leads ({counts.leads})</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-reminders-pipeline">Pipeline ({counts.pipeline})</TabsTrigger>
          {user?.role === "admin" && (
            <TabsTrigger value="pending" data-testid="tab-reminders-pending">Pending Review ({counts.pending})</TabsTrigger>
          )}
          <TabsTrigger value="expired" data-testid="tab-reminders-expired">Expired ({counts.expired})</TabsTrigger>
        </TabsList>

        {[
          ["all", notifications],
          ["emd", grouped.emd],
          ["bid", grouped.bid],
          ["leads", grouped.leads],
          ["pipeline", grouped.pipeline],
          ["pending", grouped.pending],
          ["expired", grouped.expired],
        ].map(([key, items]) => (
          <TabsContent key={key} value={key} className="mt-5">
            {isLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[0, 1].map((i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-md p-12 text-center">
                <Bell className="h-8 w-8 text-slate-300 mx-auto" />
                <div className="text-sm font-medium text-slate-700 mt-2">All clear</div>
                <div className="text-xs text-slate-500 mt-1">
                  No reminders here. Click "Run automations" to recheck.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {items.map((n) => (
                  <ReminderCard key={n.id} n={n} onAck={ack.mutate} />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
